import React, { useState, useEffect } from 'react';
import { Upload, Image, Star, MessageCircle, Eye, Edit, Save, X, Trash2 } from 'lucide-react';
import { AvatarCropper } from '../ui/avatar-cropper';
import { supabase } from '../../lib/supabase';
import { Candidate, SlideImage, Evaluation, EvaluationComment } from '../../lib/supabase';
import { UNIVERSITIES } from '../../lib/constants';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import CandidateComments from './CandidateComments';

export default function CandidateProfile() {
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [slides, setSlides] = useState<SlideImage[]>([]);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: '',

    age: '',
    university: '',
    affiliation: '',
    department: '',
    research_topic: '',
    research_overview: '',
    research_comment: '',
    has_collaborators: false,
    is_grad_student: false,
    grad_program: '',
    grad_year: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [wasAdminMode, setWasAdminMode] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '' });
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });

  const cancelledRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (user) {
        const { data: adminRow } = await supabase.from('admin_users').select('user_id').eq('user_id', user.id).maybeSingle();
        if (cancelled) return;
        setIsAdmin(!!adminRow);
      }
      try { if (!cancelled) setWasAdminMode(!!localStorage.getItem('was_admin_mode')); } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    loadCandidateData();
    return () => { cancelledRef.current = true; };
  }, []);

  const loadCandidateData = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (isCancelled() || !user) return;

      // Load candidate data
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      if (isCancelled()) return;
      if (candidateError) throw candidateError;
      if (!candidateData) return;
      setCandidate(candidateData);

      // Initialize edit data
      setEditData({
        name: candidateData.name,

        age: candidateData.age.toString(),
        university: candidateData.university || '',
        affiliation: candidateData.affiliation,
        department: candidateData.department || '',
        research_topic: candidateData.research_topic,
        research_overview: candidateData.research_overview,
        research_comment: candidateData.research_comment || '',
        has_collaborators: candidateData.has_collaborators,
        is_grad_student: !!candidateData.is_grad_student,
        grad_program: candidateData.grad_program || '',
        grad_year: candidateData.grad_year ? String(candidateData.grad_year) : ''
      });

      // Load slide images
      const { data: slidesData, error: slidesError } = await supabase
        .from('slide_images')
        .select('*')
        .eq('candidate_id', candidateData.id)
        .order('slide_index');

      if (isCancelled()) return;
      if (slidesError) throw slidesError;
      setSlides(slidesData || []);

      // Load evaluations for this candidate
      const { data: evaluationsData, error: evaluationsError } = await supabase
        .from('evaluations')
        .select(`
          *,
          evaluators (name)
        `)
        .eq('candidate_id', candidateData.id)
        .order('created_at', { ascending: false });

      if (isCancelled()) return;
      if (evaluationsError) throw evaluationsError;
      setEvaluations(evaluationsData || []);
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (candidate) {
      setEditData({
        name: candidate.name,

        age: candidate.age.toString(),
        university: candidate.university || '',
        affiliation: candidate.affiliation,
        department: candidate.department || '',
        research_topic: candidate.research_topic,
        research_overview: candidate.research_overview,
        research_comment: candidate.research_comment || '',
        has_collaborators: candidate.has_collaborators,
        is_grad_student: !!candidate.is_grad_student,
        grad_program: candidate.grad_program || '',
        grad_year: candidate.grad_year ? String(candidate.grad_year) : ''
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSaveProfile = async () => {
    if (!candidate) return;

    // Validation
    if (!editData.name.trim() || !editData.university || !editData.affiliation.trim() || !editData.research_topic.trim()) {
      setError('すべての必須項目を入力してください');
      return;
    }

    const age = parseInt(editData.age);
    if (isNaN(age) || age <= 0 || age > 100) {
      setError('年齢は1-100の範囲で入力してください');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('candidates')
        .update({
          name: editData.name.trim(),

          age: age,
          university: editData.university,
          affiliation: editData.affiliation.trim(),
          department: editData.department.trim() || undefined,
          research_topic: editData.research_topic.trim(),
          research_overview: editData.research_overview.trim(),
          research_comment: editData.research_comment.trim(),
          has_collaborators: editData.has_collaborators,
          is_grad_student: !!editData.is_grad_student,
          grad_program: editData.grad_program || null,
          grad_year: editData.grad_year ? parseInt(editData.grad_year) : null
        })
        .eq('id', candidate.id);

      if (updateError) throw updateError;

      // Update local state
      setCandidate({
        ...candidate,
        name: editData.name.trim(),

        age: age,
        university: editData.university as Candidate['university'],
        affiliation: editData.affiliation.trim(),
        department: editData.department.trim() || undefined,
        research_topic: editData.research_topic.trim(),
        research_overview: editData.research_overview.trim(),
        research_comment: editData.research_comment.trim(),
        has_collaborators: editData.has_collaborators,
        is_grad_student: !!editData.is_grad_student,
        grad_program: (editData.grad_program as any) || undefined,
        grad_year: editData.grad_year ? parseInt(editData.grad_year) : undefined
      });

      // Sync name to evaluators table
      await supabase.from('evaluators').update({
        name: editData.name.trim(),
      }).eq('email', candidate.email);

      setIsEditing(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (files: FileList | null, slideIndex: number) => {
    if (!files || files.length === 0 || !candidate) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }
    const MAX_SLIDE_BYTES = 20 * 1024 * 1024;
    if (file.size > MAX_SLIDE_BYTES) {
      setError('画像サイズが大きすぎます (上限 20MB)');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Optionally resize large images on the client before upload
      const resized = await resizeImageIfNeeded(file, { maxWidth: 1280, maxHeight: 720 });
      const uploadBlob = resized?.blob || file;
      const ext = resized?.ext || file.name.split('.').pop();

      // Upload to Supabase Storage
      const fileExt = ext;
      const fileName = `${candidate.id}/slide_${slideIndex}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('slide-images')
        .upload(fileName, uploadBlob, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('slide-images')
        .getPublicUrl(fileName);

      // Delete existing record first, then insert (avoids unique constraint error)
      await supabase
        .from('slide_images')
        .delete()
        .eq('candidate_id', candidate.id)
        .eq('slide_index', slideIndex);

      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
      const { error: dbError } = await supabase
        .from('slide_images')
        .insert({
          candidate_id: candidate.id,
          slide_index: slideIndex,
          image_url: cacheBustedUrl
        });

      if (dbError) throw dbError;

      // Reload slides
      await loadCandidateData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Resize utility: downscale JPEG/PNG if larger than constraints; pass through GIF/others
  const resizeImageIfNeeded = async (
    file: File,
    opts: { maxWidth: number; maxHeight: number }
  ): Promise<{ blob: Blob; ext: string } | null> => {
    try {
      const type = file.type.toLowerCase();
      const isJpg = type.includes('jpeg') || type.includes('jpg');
      const isPng = type.includes('png');
      const isGif = type.includes('gif');
      if (!isJpg && !isPng) {
        // Don't attempt to resize GIF or other formats
        return null;
      }

      const img = await loadImageFromFile(file);
      const { width, height } = img;
      const { maxWidth, maxHeight } = opts;

      // Always re-encode to JPEG for consistent file size
      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      const targetW = Math.round(width * scale);
      const targetH = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetW, targetH);

      const outputType = 'image/jpeg';
      const blob: Blob | null = await new Promise(resolve =>
        canvas.toBlob(b => resolve(b), outputType, 0.75)
      );
      if (!blob) return null;
      return { blob, ext: 'jpg' };
    } catch {
      return null;
    }
  };

  const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img as HTMLImageElement);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });
  };

  const handleKeySlideChange = async (slideIndex: number) => {
    if (!candidate) return;

    try {
      const { error } = await supabase
        .from('candidates')
        .update({ key_slide_index: slideIndex })
        .eq('id', candidate.id);

      if (error) throw error;

      setCandidate({ ...candidate, key_slide_index: slideIndex });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteSlide = async (slideIndex: number) => {
    if (!candidate) return;
    if (!confirm(`スライド ${slideIndex + 1} を削除しますか？`)) return;

    setError(null);
    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from('slide_images')
        .delete()
        .eq('candidate_id', candidate.id)
        .eq('slide_index', slideIndex);

      if (dbError) throw dbError;

      // Delete from storage (ignore errors - file may not exist)
      const extensions = ['jpg', 'jpeg', 'png', 'gif'];
      for (const ext of extensions) {
        await supabase.storage
          .from('slide-images')
          .remove([`${candidate.id}/slide_${slideIndex}.${ext}`])
          .catch(() => {});
      }

      // Reload slides
      await loadCandidateData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const handleAvatarSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) { setError('画像ファイルを選択してください'); return; }
    const MAX_AVATAR_BYTES = 10 * 1024 * 1024;
    if (file.size > MAX_AVATAR_BYTES) { setError('画像サイズが大きすぎます (上限 10MB)'); return; }
    const url = URL.createObjectURL(file);
    setCropImageSrc(url);
  };

  const handleAvatarCropDone = async (blob: Blob) => {
    setCropImageSrc(null);
    if (!candidate) return;
    setUploading(true);
    setError(null);
    try {
      const fileName = `${candidate.id}/avatar.jpg`;
      await supabase.storage.from('avatars').upload(fileName, blob, { upsert: true });
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;
      await supabase.from('candidates').update({ avatar_url: avatarUrl }).eq('id', candidate.id);
      await supabase.from('evaluators').update({ avatar_url: avatarUrl }).eq('email', candidate.email);
      setCandidate({ ...candidate, avatar_url: avatarUrl });
    } catch (err: any) { setError(err.message); }
    finally { setUploading(false); }
  };

  const handleDeleteAvatar = async () => {
    if (!candidate) return;
    try {
      await supabase.storage.from('avatars').remove([`${candidate.id}/avatar.jpg`]);
      await supabase.from('candidates').update({ avatar_url: null }).eq('id', candidate.id);
      await supabase.from('evaluators').update({ avatar_url: null }).eq('email', candidate.email);
      setCandidate({ ...candidate, avatar_url: undefined });
    } catch (err: any) { setError(err.message); }
  };

  if (selectedEvaluation) {
    return (
      <CandidateComments
        evaluation={selectedEvaluation}
        candidate={candidate!}
        onBack={() => setSelectedEvaluation(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <p className="text-muted-foreground">候補者データが見つかりません</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {cropImageSrc && (
        <AvatarCropper
          imageSrc={cropImageSrc}
          onCropDone={handleAvatarCropDone}
          onCancel={() => { setCropImageSrc(null); }}
        />
      )}
      {/* Avatar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              {candidate.avatar_url ? (
                <img src={candidate.avatar_url} alt="アバター" className="w-16 h-16 rounded-full object-cover border-2 border-border" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-xl font-bold text-primary border-2 border-border">
                  {candidate.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="font-medium">{candidate.name}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    <Upload className="h-3 w-3 mr-1" />
                    {candidate.avatar_url ? '変更' : 'アバター設定'}
                    <input type="file" accept="image/*" onChange={(e) => { handleAvatarSelect(e.target.files); e.target.value = ''; }} className="hidden" disabled={uploading} />
                  </label>
                </Button>
                {candidate.avatar_url && (
                  <Button variant="ghost" size="sm" onClick={handleDeleteAvatar} className="text-destructive">
                    <Trash2 className="h-3 w-3 mr-1" />
                    削除
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>アカウント設定</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setAccountOpen(v => !v)}>
              {accountOpen ? '閉じる' : '開く'}
            </Button>
          </div>
          <CardDescription>メールアドレス・パスワードの変更</CardDescription>
        </CardHeader>
        {accountOpen && (
          <CardContent className="space-y-6">
            {/* Change email */}
            <div className="space-y-2">
              <Label htmlFor="new-email">新しいメールアドレス</Label>
              <Input
                id="new-email"
                type="email"
                placeholder={candidate.email}
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ newEmail: e.target.value })}
              />
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    if (!emailForm.newEmail.trim()) return;
                    setError(null);
                    setEmailSaving(true);
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const token = session?.access_token;
                      if (!token) throw new Error('セッションが無効です');
                      const resp = await fetch('/api/update-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ newEmail: emailForm.newEmail.trim() })
                      });
                      const j = await resp.json();
                      if (!resp.ok) throw new Error(j.error || 'メール更新に失敗しました');
                      await loadCandidateData();
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setEmailSaving(false);
                    }
                  }}
                  disabled={emailSaving || !emailForm.newEmail.trim()}
                >
                  {emailSaving ? '更新中…' : 'メールを更新'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">注意: メール変更後は再ログインが必要になる場合があります。</p>
            </div>

            {/* Change password */}
            <div className="space-y-2">
              <Label htmlFor="cur-pwd">現在のパスワード</Label>
              <Input id="cur-pwd" type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} />
              <Label htmlFor="new-pwd" className="mt-2">新しいパスワード</Label>
              <Input id="new-pwd" type="password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} />
              <Label htmlFor="confirm-pwd" className="mt-2">新しいパスワード（確認）</Label>
              <Input id="confirm-pwd" type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} />
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={async () => {
                    if (!pwdForm.current || !pwdForm.next || pwdForm.next !== pwdForm.confirm) {
                      setError('パスワードを確認してください');
                      return;
                    }
                    setError(null);
                    setPasswordSaving(true);
                    try {
                      const { data: { user } } = await supabase.auth.getUser();
                      if (!user?.email) throw new Error('ユーザーが見つかりません');
                      // Re-authenticate to verify current password
                      const { error: signInErr } = await supabase.auth.signInWithPassword({ email: user.email, password: pwdForm.current });
                      if (signInErr) throw new Error('現在のパスワードが正しくありません');
                      const { error: updErr } = await supabase.auth.updateUser({ password: pwdForm.next });
                      if (updErr) throw updErr;
                      setPwdForm({ current: '', next: '', confirm: '' });
                    } catch (e: any) {
                      setError(e.message);
                    } finally {
                      setPasswordSaving(false);
                    }
                  }}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? '変更中…' : 'パスワードを変更'}
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>プロフィール情報</CardTitle>
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEdit}
              >
                <Edit className="h-4 w-4 mr-2" />
                修正
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  <X className="h-4 w-4 mr-2" />
                  キャンセル
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  保存
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">名前 *</Label>
                  <Input
                    id="edit-name"
                    value={editData.name}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                    placeholder="山田太郎"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="edit-age">年齢 *</Label>
                  <Input
                    id="edit-age"
                    type="number"
                    min="1"
                    max="100"
                    value={editData.age}
                    onChange={(e) => setEditData({...editData, age: e.target.value})}
                    placeholder="25"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-university">所属 *</Label>
                <select
                  id="edit-university"
                  required
                  className="w-full border rounded-md h-9 px-3 bg-background text-sm"
                  value={editData.university}
                  onChange={(e) => {
                    const id = e.target.value;
                    const uni = UNIVERSITIES.find((u) => u.id === id);
                    setEditData({
                      ...editData,
                      university: id,
                      affiliation: uni?.name ?? '',
                    });
                  }}
                >
                  <option value="">大学を選択してください</option>
                  {UNIVERSITIES.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-department">系・部署・診療科</Label>
                <Input
                  id="edit-department"
                  value={editData.department}
                  onChange={(e) => setEditData({...editData, department: e.target.value})}
                  placeholder="○○学部、○○部門、○○診療科など"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-topic">研究課題名 *</Label>
                <Input
                  id="edit-topic"
                  value={editData.research_topic}
                  onChange={(e) => setEditData({...editData, research_topic: e.target.value})}
                  placeholder="研究課題のタイトル"
                />
              </div>

              {/* 大学院生情報（任意・条件付き） */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-is-grad"
                    checked={editData.is_grad_student}
                    onCheckedChange={(checked) => setEditData({...editData, is_grad_student: !!checked})}
                  />
                  <Label htmlFor="edit-is-grad" className="text-sm">大学院生（修士/博士）</Label>
                </div>
                {editData.is_grad_student && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="edit-grad-program">課程（博士・修士）</Label>
                      <select
                        id="edit-grad-program"
                        className="w-full border rounded-md h-9 px-3 bg-background"
                        value={editData.grad_program}
                        onChange={(e) => setEditData({...editData, grad_program: e.target.value})}
                      >
                        <option value="">選択してください</option>
                        <option value="doctor">博士</option>
                        <option value="master">修士</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-grad-year">学年</Label>
                      <Input
                        id="edit-grad-year"
                        type="number"
                        min="1"
                        max="6"
                        value={editData.grad_year}
                        onChange={(e) => setEditData({...editData, grad_year: e.target.value})}
                        placeholder="1"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-collaborators"
                  checked={editData.has_collaborators}
                  onCheckedChange={(checked) => setEditData({...editData, has_collaborators: !!checked})}
                />
                <Label htmlFor="edit-collaborators" className="text-sm">
                  共同研究者がいる
                </Label>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                プロフィール情報は折りたたまれています。<span className="hidden md:inline">「修正」を押すと編集フォームが展開されます。</span>
              </div>
              <div className="text-sm">
                <span className="text-foreground font-medium mr-2">{candidate.name}</span>
                <span className="text-muted-foreground">/ {candidate.affiliation}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rating Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Star className="h-5 w-5 mr-2 text-yellow-500" />
            評価サマリー
          </CardTitle>
          <CardDescription>
            参加者からの星評価の平均値と件数を表示します（星0は評価なし）。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            const rated = (evaluations || []).filter((e: any) => (e.rating || 0) > 0);
            const count = rated.length;
            const avg = count > 0 ? (rated.reduce((s: number, e: any) => s + Math.min(5, Math.max(0, e.rating || 0)), 0) / count) : 0;
            const rounded = Math.max(0, Math.min(5, Math.round(avg * 2) / 2));
            return (
              <div className="flex items-center gap-4">
                <div className="flex items-center">
                  {[1,2,3,4,5].map(i => {
                    const fill = rounded >= i ? 1 : (rounded >= i - 0.5 ? 0.5 : 0);
                    if (fill === 1) {
                      return (
                        <svg key={i} viewBox="0 0 24 24" className="h-5 w-5 text-yellow-500" fill="currentColor" aria-hidden="true">
                          <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                        </svg>
                      );
                    }
                    if (fill === 0.5) {
                      return (
                        <span key={i} className="relative inline-block h-5 w-5 align-middle">
                          <svg viewBox="0 0 24 24" className="absolute inset-0 h-5 w-5 text-muted-foreground" fill="currentColor" aria-hidden="true">
                            <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                          </svg>
                          <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                            <svg viewBox="0 0 24 24" className="h-5 w-5 text-yellow-500" fill="currentColor" aria-hidden="true">
                              <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                            </svg>
                          </span>
                        </span>
                      );
                    }
                    return (
                      <svg key={i} viewBox="0 0 24 24" className="h-5 w-5 text-muted-foreground" fill="currentColor" aria-hidden="true">
                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                      </svg>
                    );
                  })}
                </div>
                <div className="text-base font-medium">{avg.toFixed(1)} / 5</div>
                <div className="text-sm text-muted-foreground">評価数 {count}</div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Evaluations and Comments */}
      {evaluations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              コメント
            </CardTitle>
            <CardDescription>
              参加者からのコメントを確認し、返信することができます
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {evaluations.map((evaluation: any) => (
                <div
                  key={evaluation.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{evaluation.evaluators?.name || '匿名参加者'}</p>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      {evaluation.liked && <span className="text-red-500">❤️ いいね</span>}
                      {evaluation.want_to_participate && <span className="text-green-600">🙋 参加希望</span>}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedEvaluation(evaluation)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    コメントを見る
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Research Overview & Comment (independent section) */}
      <Card>
        <CardHeader>
          <CardTitle>研究紹介</CardTitle>
          <CardDescription>研究概要とマッチングイベントへのコメントを編集できます。ダッシュボードや詳細画面に表示されます。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="research-overview">研究概要（100字程度）</Label>
            <Textarea
              id="research-overview"
              rows={3}
              value={editData.research_overview}
              onChange={(e) => setEditData({...editData, research_overview: e.target.value})}
              placeholder="研究の概要を簡潔に記述してください"
            />
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">推奨: 100字程度</span>
              <span className={editData.research_overview.length > 150 ? 'text-destructive' : editData.research_overview.length >= 50 ? 'text-green-600' : 'text-muted-foreground'}>
                {editData.research_overview.length}字
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="research-comment">マッチングイベントへのコメント（250〜350字程度）</Label>
            <Textarea
              id="research-comment"
              rows={6}
              value={editData.research_comment}
              onChange={(e) => setEditData({...editData, research_comment: e.target.value})}
              placeholder="今回のマッチングイベントに向けて、あなたの研究について簡単にコメントしてください。どのような共同研究に興味があるか、どんなスキルや知見を提供できるかなどを記載してください。"
            />
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">推奨: 250〜350字</span>
              <span className={editData.research_comment.length > 400 ? 'text-destructive' : editData.research_comment.length >= 250 ? 'text-green-600' : 'text-muted-foreground'}>
                {editData.research_comment.length}字
              </span>
            </div>
          </div>

          <Button
            onClick={async () => {
              if (!candidate) return;
              setSaving(true);
              setError(null);
              try {
                const { error } = await supabase
                  .from('candidates')
                  .update({
                    research_overview: editData.research_overview.trim(),
                    research_comment: editData.research_comment.trim(),
                  })
                  .eq('id', candidate.id);
                if (error) throw error;
                setCandidate({
                  ...candidate,
                  research_overview: editData.research_overview.trim(),
                  research_comment: editData.research_comment.trim(),
                });
              } catch (err: any) {
                setError(err.message);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? '保存中...' : '研究紹介を保存'}
          </Button>
        </CardContent>
      </Card>

      {/* Slide Upload */}
      <Card>
        <CardHeader>
          <CardTitle>スライド画像（4枚）</CardTitle>
          <CardDescription>
            研究発表用のスライド画像を4枚アップロードしてください。アップロード後、キースライドを1枚選択してください。
          </CardDescription>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-2">💡 PowerPointファイルをお持ちの方へ</p>
            <p className="text-sm text-blue-800 mb-2">PowerPointから画像に変換してアップロードしてください：</p>
            <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
              <li>PowerPointで「ファイル」→「エクスポート」→「ファイル形式の変更」を選択</li>
              <li>「PNG」または「JPEG」形式を選択してエクスポート</li>
              <li>保存された画像ファイルをここにアップロード</li>
            </ol>
            <p className="text-xs text-blue-700 mt-2">※ 推奨形式：JPEG、PNG（最大1280×720pxに自動リサイズされます）</p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[0, 1, 2, 3].map((slideIndex) => {
              const slide = slides.find(s => s.slide_index === slideIndex);
              const isKeySlide = candidate.key_slide_index === slideIndex;


              return (
                <Card key={slideIndex} className={`${isKeySlide ? 'ring-2 ring-yellow-500' : ''}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">スライド {slideIndex + 1}</CardTitle>
                      {isKeySlide && (
                        <Badge className="bg-yellow-500 hover:bg-yellow-600">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          キー
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {slide ? (
                      <div className="space-y-4">
                        <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                          <img
                            src={slide.image_url}
                            alt={`スライド ${slideIndex + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button variant="outline" size="sm" className="flex-1" asChild>
                            <label className="cursor-pointer">
                              <Upload className="h-4 w-4 mr-2" />
                              画像を変更
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e.target.files, slideIndex)}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          </Button>
                          {!isKeySlide && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleKeySlideChange(slideIndex)}
                              className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                            >
                              <Star className="h-4 w-4 mr-2" />
                              キーに設定
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteSlide(slideIndex)}
                            className="text-destructive border-destructive/30 hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            削除
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center space-y-4">
                        <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-border">
                          <Image className="h-12 w-12 text-muted-foreground" />
                        </div>
                        <div className="space-y-2">
                          <Button asChild>
                            <label className="cursor-pointer">
                              <Upload className="h-4 w-4 mr-2" />
                              画像をアップロード
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileUpload(e.target.files, slideIndex)}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            PNG, JPG, GIF（最大1280×720pxに自動リサイズ）
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>


          {uploading && (
            <div className="mt-6 text-center">
              <div className="inline-flex items-center px-4 py-2 bg-muted rounded-md">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                <span className="text-muted-foreground">アップロード中...</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone: Account Deletion */}
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">アカウントの削除</CardTitle>
          <CardDescription>
            この操作は取り消せません。発表内容・画像・コメントなど、関連データも削除されます。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!confirmDelete ? (
            <Button
              variant="destructive"
              onClick={() => { setConfirmDelete(true); setDeleteEmailInput(''); }}
            >
              アカウントを削除する
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">本当に削除しますか？この操作は元に戻せません。</p>
              <div className="space-y-2">
                <Label htmlFor="delete-confirm-email">確認のため、登録メールアドレスを入力してください</Label>
                <Input
                  id="delete-confirm-email"
                  type="email"
                  autoComplete="off"
                  value={deleteEmailInput}
                  onChange={(e) => setDeleteEmailInput(e.target.value)}
                  placeholder={candidate?.email || ''}
                  disabled={deleting}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setConfirmDelete(false); setDeleteEmailInput(''); }}
                  disabled={deleting}
                >
                  キャンセル
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    setDeleting(true);
                    setError(null);
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const token = session?.access_token;
                      if (!token) throw new Error('セッションが無効です');
                      const resp = await fetch('/api/delete-account', {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      });
                      if (!resp.ok) {
                        const j = await resp.json().catch(() => ({}));
                        throw new Error(j.error || '削除に失敗しました');
                      }
                      await supabase.auth.signOut();
                      window.location.reload();
                    } catch (e: any) {
                      setError(e.message);
                      setConfirmDelete(false);
                      setDeleteEmailInput('');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting || !candidate?.email || deleteEmailInput.trim() !== candidate.email}
                >
                  {deleting ? '削除中…' : '完全に削除する'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {(isAdmin || wasAdminMode) && (
        <Card>
          <CardHeader>
            <CardTitle>管理者メニュー</CardTitle>
            <CardDescription>管理者モードに切り替えると、全ユーザーの編集・削除、評価（いいね/参加希望）調整が可能です。</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Button onClick={() => { localStorage.setItem('goto_admin', '1'); window.location.reload(); }}>
                    管理者モードへ
                  </Button>
                )}
                {wasAdminMode && (
                  <Button variant="ghost" onClick={() => { localStorage.setItem('goto_normal', 'candidates'); window.location.reload(); }}>
                    通常モードへ
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
